import * as styles from "./Section.css";

import { clsx } from "clsx";
import type { JSX, ReactNode } from "react";

import { FieldGroup } from "@frontend/components/atoms/Field";
import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import type { IconName } from "@frontend/components/atoms/Icon";

export type SplitSectionProps = {
  icon?: IconName;
  legend: ReactNode;
  left: ReactNode;
  right: ReactNode;
};

export type SectionProps = {
  icon?: IconName;
  legend: ReactNode;
  className?: string;
  /** Whether a column is kept beside the fields for an action button. */
  gutter?: boolean;
  children: ReactNode;
};

// Every section reports its errors in a bubble over the control rather than
// under it: the tabs hold many fields in a fixed height, and a sentence pushed
// into the flow would shift the rows below on every check.

export function SectionFrame({
  icon,
  legend,
  className,
  children,
}: SectionProps): JSX.Element {
  return (
    <Fieldset variant="section" errorDisplay="tooltip" className={className}>
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      {children}
    </Fieldset>
  );
}

export function Section({
  icon,
  legend,
  className,
  gutter = true,
  children,
}: SectionProps): JSX.Element {
  return (
    <Fieldset
      variant="section"
      errorDisplay="tooltip"
      className={clsx(styles.section, !gutter && styles.ungutted, className)}
    >
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      <FieldGroup orientation="horizontal" labelAlign="right">
        {children}
      </FieldGroup>
    </Fieldset>
  );
}

export function SplitSection({
  icon,
  legend,
  left,
  right,
}: SplitSectionProps): JSX.Element {
  return (
    <Fieldset
      variant="section"
      errorDisplay="tooltip"
      className={styles.section}
    >
      <FieldsetLegend icon={icon}>{legend}</FieldsetLegend>

      <div className={styles.split}>
        <FieldGroup orientation="horizontal" labelAlign="right">
          {left}
        </FieldGroup>

        <FieldGroup orientation="horizontal" labelAlign="right">
          {right}
        </FieldGroup>
      </div>
    </Fieldset>
  );
}
